import React, { useMemo, useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Print } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { CLIENT_CONTRACT_TERMS_ENDPOINT } from '../../../../consts';
import { useHasPermission, useTenantName } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import GridAddressCell from '../../../../components/ui/gridAddressCell/gridAddressCell.component';
import EntityReferenceLink from '../../../../components/ui/entityReference/entityReferenceLink.component';
import { formatCounterValue } from '../../../../utils';
import { ClientContractTermPagesLimit } from '../../../../types/contracts';
import { EQUIPMENT_KIND_OPTIONS, EQUIPMENT_OWNERSHIP_OPTIONS } from '../../../../consts';
import {
  containsStringFilterOperators,
  singleSelectFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';
import { countryName } from "../../../../utils/countries";

export const ClientEquipmentsActive = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const canViewEquipments = useHasPermission("settlement.view_equipment");
  const canViewContracts = useHasPermission("settlement.view_contract");
  // No installation address means the machine is not standing at the client's
  // site any more - it is back with us.
  const tenantName = useTenantName();

  const OWNERSHIP_OPTIONS = useMemo(
    () => EQUIPMENT_OWNERSHIP_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const KIND_OPTIONS = useMemo(
    () => EQUIPMENT_KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  useEffect(() => {
    setOverride(location.pathname, t('Active equipment'));
    return () => {
      clearOverride(location.pathname);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const columns: GridColDef[] = useMemo(() => [
    {
      // Filtering not wired: the queryset this endpoint filters is
      // ContractTerm, fanned out to one row per (term, equipment) pair after
      // filter_queryset() runs (see ClientContractTermViewSet.list) - but the
      // "id" shown here is the equipment's id, not the ContractTerm's. A
      // ContractTerm-level id filter would silently search the wrong id
      // relative to what a user sees and types here.
      field: "id",
      headerName: "ID",
      filterable: false,
    },
    {
      field: "contract__number",
      headerName: t("Contract Number"),
      filterOperators: containsStringFilterOperators(),
      valueGetter: (value, row) => row.contract?.number,
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="contract"
          entityId={params.row.contract?.id}
          label={params.row.contract?.number || "-"}
          canPreview={canViewContracts}
        />
      ),
    },
    {
      field: "contract__internal_name",
      headerName: t("Internal name"),
      filterOperators: containsStringFilterOperators(),
      valueGetter: (value, row) => row.contract?.internal_name,
    },
    {
      field: "equipment__name",
      headerName: t("Equipment"),
      filterable: false,
      valueGetter: (value, row) => row.equipment?.full_name,
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="equipment"
          entityId={params.row.equipment?.id}
          label={params.row.equipment?.full_name || "-"}
          canPreview={canViewEquipments}
        />
      ),
    },
    {
      field: "equipment__ownership",
      headerName: t("Ownership"),
      sortable: false,
      type: "singleSelect",
      valueOptions: OWNERSHIP_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.equipment?.ownership,
      renderCell: (params) => params.row.equipment?.ownership_display,
    },
    {
      field: "equipment__kind",
      headerName: t("Kind"),
      sortable: false,
      type: "singleSelect",
      valueOptions: KIND_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.equipment?.kind,
      renderCell: (params) => params.row.equipment?.kind_display,
    },
    {
      field: "equipment__is_installed",
      headerName: t("Is installed"),
      type: "boolean",
      sortable: false,
      valueGetter: (value, row) => row.equipment?.is_installed,
    },
    {
      field: "installation_address",
      headerName: t("Installation address"),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <GridAddressCell
            address={params.row.equipment?.installation_address}
            fallbackText={tenantName}
          />
        );
      },
      valueGetter: (value, row) => {
        const address = row.equipment?.installation_address;
        if (!address) {
          return tenantName;
        }
        return `${address.street}, ${address.city}, ${address.zip_code}, ${countryName(address.country)}`;
      },
    },
    {
      field: "pages_limits",
      headerName: t("Pages in package"),
      sortable: false,
      filterable: false,
      valueGetter: (value, row) =>
        (row.pages_limits || [])
          .map((limit: ClientContractTermPagesLimit) => `${limit.kind_display}: ${formatCounterValue(limit.included_in_rent)}`)
          .join(", "),
      renderCell: (params) => {
        const limits: ClientContractTermPagesLimit[] = params.row.pages_limits || [];
        if (!limits.length) {
          return "-";
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            {limits.map((limit, index) => (
              <Typography key={index} variant="body2" sx={{ lineHeight: 1.1 }}>
                {`${limit.kind_display}: ${formatCounterValue(limit.included_in_rent)}`}
              </Typography>
            ))}
          </Box>
        );
      },
    },
  ], [t, canViewEquipments, canViewContracts, tenantName, OWNERSHIP_OPTIONS, KIND_OPTIONS]);

  const handleRowClick = (equipmentId: string) => {
    navigate(`/equipment-section/equipments/${equipmentId}`);
  };

  if (!canViewEquipments) {
    return null;
  }

  const activeEquipmentsUrl = `${CLIENT_CONTRACT_TERMS_ENDPOINT}?client_id=${id}&is_active=true`;

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<Print />}
        title={t('Active equipment')}
        url={activeEquipmentsUrl}
        gridSettingsKey="CLIENT_ACTIVE_EQUIPMENTS"
        columns={columns}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
